import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  FollowupHistoryRow,
  FollowupSegmentStatus,
  FollowupSegmentType,
} from '../../helpers/webinarMailingData';
import moment from 'moment';

const FollowupHistoryTab: React.FC = () => {
  const [data, setData] = useState<FollowupHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FollowupSegmentStatus[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      statusFilter.forEach((s) => params.append('status', s));
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetchData<FollowupHistoryRow[]>(
        `/api/webinar-followups/history?${params.toString()}`,
      );

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [statusFilter, startDate, endDate]);

  const getStatusChip = (status: FollowupSegmentStatus) => {
    const config: Record<FollowupSegmentStatus, { color: string; label: string }> = {
      draft: { color: '#757575', label: 'Entwurf' },
      awaiting_webinar: { color: '#ff9800', label: 'Wartet' },
      scheduled: { color: '#1976d2', label: 'Geplant' },
      sent: { color: '#2e7d32', label: 'Gesendet' },
      cancelled: { color: '#ed6c02', label: 'Abgebrochen' },
    };
    const { color, label } = config[status];
    return (
      <Chip
        size="small"
        label={label}
        sx={{
          backgroundColor: color,
          color: 'white',
          textDecoration: status === 'cancelled' ? 'line-through' : 'none',
        }}
      />
    );
  };

  const getSegmentTypeChip = (type: FollowupSegmentType) => {
    const isRNA = type === 'non_attendee';
    return (
      <Tooltip title={isRNA ? 'Registriert, Nicht Anwesend' : 'Anwesend, Nicht Gekauft'}>
        <Chip
          size="small"
          label={isRNA ? 'RNA' : 'ANO'}
          sx={{
            backgroundColor: isRNA ? '#9c27b0' : '#00bcd4',
            color: 'white',
            minWidth: 50,
          }}
        />
      </Tooltip>
    );
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const handleStatusChange = (event: SelectChangeEvent<FollowupSegmentStatus[]>) => {
    const value = event.target.value;
    setStatusFilter(
      typeof value === 'string' ? (value.split(',') as FollowupSegmentStatus[]) : value,
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Nachfass-Verlauf
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select<FollowupSegmentStatus[]>
            multiple
            value={statusFilter}
            onChange={handleStatusChange}
            label="Status"
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            <MenuItem value="draft">Entwurf</MenuItem>
            <MenuItem value="scheduled">Geplant</MenuItem>
            <MenuItem value="sent">Gesendet</MenuItem>
            <MenuItem value="cancelled">Abgebrochen</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label="Von"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />

        <TextField
          size="small"
          type="date"
          label="Bis"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Kampagne
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Webinar
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Segment
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Empfänger
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Status
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Geplant
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Gesendet
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Opens
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Open%
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Clicks
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                CTR
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Keine Daten gefunden
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.segment_id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {row.campaign_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {row.webinar_title || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getSegmentTypeChip(row.segment_type)}</TableCell>
                  <TableCell align="right">{formatNumber(row.recipient_count, 0)}</TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                  <TableCell>
                    {row.scheduled_datetime
                      ? moment.utc(row.scheduled_datetime).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {row.sent_at ? moment.utc(row.sent_at).format('DD.MM.YYYY HH:mm') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.metrics ? formatNumber(row.metrics.opens, 0) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.metrics ? formatPercent(row.metrics.openRate) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.metrics ? formatNumber(row.metrics.clicks, 0) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.metrics ? formatPercent(row.metrics.ctr) : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FollowupHistoryTab;
